import { useMemo, useState } from 'react';
import { Trophy } from 'lucide-react';
import { useModelPerformance } from '../../queries/modelPerformance';
import { aggregateModels, ModelAggregate, presentSegments, roiPct, TrackSegment } from './model-performance-helpers';
import { formatRoi, winPercentage, winPercentageClass } from '../../lib/history-performance';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';

const SEGMENT_LABEL: Record<TrackSegment, string> = {
  all: 'All',
  metro: 'Metro',
  provincial: 'Provincial',
  country: 'Country',
};

function Strike({ label, winners, races, roi }: { label: string; winners: number; races: number; roi: number }) {
  const pct = winPercentage(winners, races);
  const r = formatRoi(Number.isNaN(roi) ? null : roi);
  return (
    <span className="whitespace-nowrap">
      <span className="text-muted-foreground">{label}</span> <span className="font-semibold text-foreground">{winners}/{races}</span>{' '}
      <span className={winPercentageClass(pct)}>({pct}%)</span> <span className={r.className}>{r.text}</span>
    </span>
  );
}

function ModelRow({ model, leading }: { model: ModelAggregate; leading: boolean }) {
  return (
    <div className={`rounded-lg border p-3 ${leading ? 'border-emerald-500/40 bg-emerald-500/5' : 'border-border'}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="flex items-center gap-1.5 text-sm font-semibold">
          {leading && <Trophy className="h-3.5 w-3.5 text-emerald-600" />}
          {[model.ai_model, model.ai_version].filter(Boolean).join(' ') || 'Unknown'}
        </span>
        {leading && (
          <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400">
            LEADING
          </span>
        )}
      </div>
      <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs">
        <Strike label="Top" winners={model.topWinners} races={model.topRaces} roi={roiPct(model.topReturn, model.topStaked)} />
        <span className="whitespace-nowrap">
          <span className="text-muted-foreground">Total</span> <span className="font-semibold text-foreground">{model.allWinners}</span>
        </span>
        <Strike label="Value" winners={model.valueWinners} races={model.valueRaces} roi={roiPct(model.valueReturn, model.valueStaked)} />
      </div>
    </div>
  );
}

export function ModelPerformancePanel({ date }: { date: string }) {
  const { data, isLoading } = useModelPerformance(date);
  const rows = data?.models ?? [];
  const [segment, setSegment] = useState<TrackSegment>('all');

  const segments = useMemo<TrackSegment[]>(() => ['all', ...presentSegments(rows)], [rows]);
  // If the active segment vanishes (date change), fall back to All.
  const activeSegment = segments.includes(segment) ? segment : 'all';
  const models = useMemo(() => aggregateModels(rows, activeSegment), [rows, activeSegment]);

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm">Model performance — this day, all meetings</CardTitle>
        {segments.length > 1 && (
          <div className="mt-2 flex flex-wrap gap-1">
            {segments.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setSegment(s)}
                className={`rounded-md px-2.5 py-1 text-xs font-medium ${
                  activeSegment === s ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-accent'
                }`}
              >
                {SEGMENT_LABEL[s]}
              </button>
            ))}
          </div>
        )}
      </CardHeader>
      <CardContent className="pt-0">
        {isLoading ? (
          <p className="py-4 text-sm text-muted-foreground">Loading model performance…</p>
        ) : models.length === 0 ? (
          <p className="py-4 text-sm text-muted-foreground">No settled model results for this day yet.</p>
        ) : (
          <div className="space-y-2">
            {models.map((m, i) => (
              <ModelRow key={m.key} model={m} leading={i === 0 && models.length > 1} />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
